import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnChanges,
	Renderer2,
	SimpleChanges,
	inject,
	output,
	viewChild,
	input,
	InputSignal,
	Signal,
	OutputEmitterRef
} from '@angular/core';
import { Languages, RTL_TOOLTIP_CLASS, Directions } from '@core';
import {
	SuggestionCard,
	SuggestionCardConfig,
	SuggestionMenuOption,
	TimeOfDayColor
} from '@app/shared/ui/components/suggestion-card/suggestion-card.helper';
import { Params } from '@angular/router';
import { DEFAULT_TABLE_PAGE_SIZE } from '@app/shared/ag-grid/ag-grid.consts';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export type MenuOptionsEvent = {
	action: number;
	card: SuggestionCard;
	index: number;
};

@Component({
	selector: 'suggestion-card',
	templateUrl: './suggestion-card.component.html',
	styleUrls: ['./suggestion-card.component.scss'],
	standalone: false
})
export class SuggestionCardComponent implements AfterViewInit, OnChanges {
	private renderer = inject(Renderer2);
	private translateService = inject(TranslateService);

	readonly suggestionsListElement: Signal<ElementRef<HTMLDivElement>> =
		viewChild<ElementRef<HTMLDivElement>>('suggestionsList');

	@Input() set data(val: SuggestionCard[]) {
		this._data = val;
	}
	readonly config: InputSignal<SuggestionCardConfig> = input.required<SuggestionCardConfig>();
	readonly removeCardIndex: InputSignal<number> = input<number>(undefined);
	readonly callback: InputSignal<(parameters: Params) => Observable<any>> = input<
		(parameters: Params) => Observable<any>
	>((parameters: Params) => {
		throw Error('Please provide an optionsCallback for the "SuggestionCardComponent"');
	});

	readonly menuOptionSelected: OutputEmitterRef<MenuOptionsEvent> = output<MenuOptionsEvent>();
	readonly optionSelected: OutputEmitterRef<SuggestionCard> = output<SuggestionCard>();
	readonly scrollEvent: OutputEmitterRef<Params> = output<Params>();

	private selectedCard: SuggestionCard;
	private paginationTimeout: any;
	private pageCounter: number = 1;
	private hasRequestedForAllData: boolean = false;

	public _data: SuggestionCard[];
	public timeOfDayColor = TimeOfDayColor;
	public tooltipClass: string;
	public sideLabelDirection: string;
	public suggestionMenuOption = SuggestionMenuOption;

	constructor() {
		this.tooltipClass = this.translateService.currentLang === Languages.HE ? RTL_TOOLTIP_CLASS : null;
		this.sideLabelDirection = this.translateService.currentLang === Languages.HE ? Directions.Rtl : Directions.Ltr;
	}

	ngAfterViewInit(): void {
		this.listenToListElementScrollEvent();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.removeCardIndex && !changes.removeCardIndex?.firstChange) {
			this._data.splice(changes.removeCardIndex.currentValue, 1);
		}
	}

	private listenToListElementScrollEvent(): void {
		const suggestionsListElement = this.suggestionsListElement();
		if (!suggestionsListElement) {
			throw Error('The list element is undefined.');
		}
		this.renderer.listen(suggestionsListElement.nativeElement, 'scroll', event => {
			clearTimeout(this.paginationTimeout);
			this.paginationTimeout = setTimeout(() => {
				const reachedEndOfList: boolean =
					event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 1;
				if (reachedEndOfList && !this.hasRequestedForAllData) {
					const params: Params = {
						limit: DEFAULT_TABLE_PAGE_SIZE,
						offset: DEFAULT_TABLE_PAGE_SIZE * this.pageCounter
					};
					this.scrollEvent.emit(params); //TODO: Check if needed or handle in parent component
					this.callback()(params).subscribe(res => {
						if (!!res.length) {
							this._data = [...this._data, ...res];
							this.pageCounter++;
						} else {
							this.hasRequestedForAllData = true;
						}
					});
				}
			}, 100);
		});
	}

	public actionFromIcon(action: number, index: number): void {
		const card: SuggestionCard = this._data[index];
		this.menuOptionSelected.emit({ action, card, index });
	}

	public onOptionDoubleClicked(selectedIndex: number): void {
		const isSelected: boolean = (this._data[selectedIndex].isSelected = !this._data[selectedIndex].isSelected);
		this.selectedCard = isSelected ? this._data[selectedIndex] : null;
		this._data = this._data.map((item: SuggestionCard, index: number) => {
			if (index === selectedIndex) {
				return { ...item, isSelected };
			}
			return { ...item, isSelected: false };
		});
		this.optionSelected.emit(this.selectedCard);
	}
}
